import type { Species } from "@/utils/nutritionMath";

const affiliateLinks: Partial<Record<"default" | "weight_loss" | "kitten" | "puppy", string>> = {};

export const dogStatuses = [
  { value: "neutered_adult", label: "Neutered adult", detail: "Typical adult dog after spay/neuter" },
  { value: "intact_adult", label: "Intact adult", detail: "Adult dog that is not spayed/neutered" },
  { value: "obesity_prone", label: "Obesity prone", detail: "Lower-energy estimate for easy weight gain" },
  { value: "weight_loss", label: "Weight loss", detail: "Lower-calorie starting point" },
  { value: "puppy_under_4m", label: "Puppy under 4 months", detail: "Rapid growth phase" },
  { value: "puppy_over_4m", label: "Puppy over 4 months", detail: "Growth after early puppy phase" },
] as const;

export const catStatuses = [
  { value: "neutered_adult", label: "Neutered adult", detail: "Typical adult cat after spay/neuter" },
  { value: "intact_adult", label: "Intact adult", detail: "Adult cat that is not spayed/neutered" },
  { value: "weight_loss", label: "Weight loss", detail: "Lower-calorie starting point" },
  { value: "kitten", label: "Kitten", detail: "Growth estimate for kittens" },
] as const;

export const statusLabels = [...dogStatuses, ...catStatuses].reduce<Record<string, string>>((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

export const references = [
  {
    title: "Pet Nutrition Alliance Calorie Calculator",
    source: "Pet Nutrition Alliance",
    url: "https://petnutritionalliance.org/resources/calorie-calculator",
    note: "A practical veterinary calorie calculator for dogs and cats. Useful for checking calorie estimates and healthy weight-loss monitoring rates.",
  },
  {
    title: "WSAVA Feeding Instructions and Monitoring Chart",
    source: "World Small Animal Veterinary Association",
    url: "https://wsava.org/wp-content/uploads/2020/01/Feeding-Instructions-and-Monitoring-Chart-for-Hospitalized-Patients.pdf",
    note: "Shows the RER formula and how to convert daily calories into grams, milliliters, or cups using the food's calorie density.",
  },
  {
    title: "WSAVA Selecting a Pet Food for Your Pet",
    source: "World Small Animal Veterinary Association",
    url: "https://wsava.org/wp-content/uploads/2021/04/Selecting-a-pet-food-for-your-pet-updated-2021_WSAVA-Global-Nutrition-Toolkit.pdf",
    note: "Pet-owner checklist for choosing food and understanding what label information can and cannot tell you.",
  },
  {
    title: "AAHA Weight Reduction in the Obese Pet",
    source: "American Animal Hospital Association",
    url: "https://www.aaha.org/resources/2021-aaha-nutrition-and-weight-management-guidelines/weight-reduction-in-the-obese-pet/",
    note: "Plain-language veterinary guidance on weight reduction, monitoring progress, and why weight-loss plans should be supervised.",
  },
  {
    title: "Calories",
    source: "AAFCO",
    url: "https://www.aafco.org/consumers/understanding-pet-food/calories/",
    note: "Explains why calorie statements are expressed as kcal/kg as fed and how that helps compare pet foods more reliably.",
  },
  {
    title: "Reading Pet Food Labels",
    source: "AAFCO",
    url: "https://www.aafco.org/consumers/understanding-pet-food/reading-labels/",
    note: "Explains US calorie-content statements, nutritional adequacy wording, and why feeding directions are only guidelines.",
  },
  {
    title: "Deciphering the Pet Food Label",
    source: "American Animal Hospital Association",
    url: "https://www.aaha.org/wp-content/uploads/globalassets/02-guidelines/weight-management/Weight-Management-Pet-Food-Labels",
    note: "Pet food label tip sheet explaining calorie density, as-fed values, and why water content changes calorie comparisons.",
  },
  {
    title: "Selecting the Right Pet Food",
    source: "AAFCO",
    url: "https://www.aafco.org/consumers/understanding-pet-food/selecting-the-right-pet-food/",
    note: "Consumer guide to life-stage wording, complete-and-balanced statements, and why calorie-rich foods may not suit every pet.",
  },
  {
    title: "Understanding Pet Food Labels",
    source: "FEDIAF EuropeanPetFood",
    url: "https://europeanpetfood.org/pet-food-facts/fact-sheets/nutrition/understanding-pet-food-labels/",
    note: "European pet-owner guide to composition, analytical constituents, feeding guidelines, and other common label sections.",
  },
  {
    title: "FEDIAF Nutritional Guidelines for Cats and Dogs",
    source: "FEDIAF EuropeanPetFood",
    url: "https://europeanpetfood.org/self-regulation/nutritional-guidelines/",
    note: "European nutrition reference for complete and complementary pet food, useful for understanding the standards behind many EU/UK products.",
  },
  {
    title: "Complete and Balanced Pet Food",
    source: "U.S. Food and Drug Administration",
    url: "https://www.fda.gov/animal-veterinary/animal-health-literacy/complete-and-balanced-pet-food",
    note: "Consumer explanation of complete-and-balanced claims, AAFCO statements, and why wet and dry foods should be compared carefully.",
  },
  {
    title: "Animal Food Labeling and Pet Food Claims",
    source: "U.S. Food and Drug Administration",
    url: "https://www.fda.gov/animal-veterinary/animal-foods-feeds/animal-food-labeling-and-pet-food-claims",
    note: "Overview of US animal-food label oversight and the role of labels in safe and effective use of pet food.",
  },
];

export const recommendationCopy = {
  weight_loss: {
    title: "Weight-loss plans need monitoring",
    text: "Use this as a starting estimate, then monitor weight and body condition. Cats and pets with health problems should have a vet-guided plan.",
  },
  kitten: {
    title: "Choose food for growth",
    text: "Kittens need food intended for growth or all life stages, plus frequent portion updates as their weight changes.",
  },
  puppy: {
    title: "Choose food for growth",
    text: "Puppies should eat food intended for growth. Large-breed puppies may need food appropriate for their expected adult size.",
  },
  default: {
    title: "Use the exact food label",
    text: "Different dry and wet foods can have very different calories. Use the kcal/kg or kcal/100g value from the exact product you feed.",
  },
};

const getAffiliateKey = (species: Species, status: string): keyof typeof affiliateLinks => {
  if (status === "weight_loss") return "weight_loss";
  if (status === "kitten") return "kitten";
  if (status.startsWith("puppy")) return "puppy";
  if (species === "cat") return "default";
  return "default";
};

export const getResultRecommendation = (species: Species, status: string) => {
  const affiliateKey = getAffiliateKey(species, status);
  const recommendation =
    status === "weight_loss"
      ? recommendationCopy.weight_loss
      : status === "kitten"
        ? recommendationCopy.kitten
        : status.startsWith("puppy")
          ? recommendationCopy.puppy
          : recommendationCopy.default;

  return {
    ...recommendation,
    affiliateUrl: affiliateLinks[affiliateKey],
  };
};
